import React from 'react';
import { Box, Card, CardActionArea, CardContent, Grid, Typography, alpha, useTheme } from '@mui/material';
import {
  Explore as ExploreIcon,
  AccountTree as MappingIcon,
} from '@mui/icons-material';

interface BatchFlowCardProps {
  batchId: string;
  batchStatus: string;
  hasTargetSchema: boolean;
  hasStagingOutput: boolean;
  onExploreData: () => void;
  onGenerateMapping: () => void;
}

const BatchFlowCard: React.FC<BatchFlowCardProps> = ({
  batchStatus,
  hasTargetSchema,
  hasStagingOutput,
  onExploreData,
  onGenerateMapping,
}) => {
  const theme = useTheme();
  const canExplore = !['pending', 'profiling'].includes(batchStatus);
  const canMap = hasTargetSchema;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 11 }}>
        What's Next?
      </Typography>
      <Grid container spacing={2}>
        {/* Explore Data */}
        <Grid item xs={12} sm={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              transition: 'all 0.2s',
              cursor: canExplore ? 'pointer' : 'not-allowed',
              opacity: canExplore ? 1 : 0.5,
              '&:hover': canExplore ? {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.shadows[4],
              } : {},
            }}
          >
            <CardActionArea
              onClick={canExplore ? onExploreData : undefined}
              disabled={!canExplore}
              sx={{ height: '100%', p: 2.5 }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ExploreIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Explore Data
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                  Open the Workspace to research, clean, and prepare this data before mapping.
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* Generate Mapping */}
        <Grid item xs={12} sm={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              transition: 'all 0.2s',
              cursor: canMap ? 'pointer' : 'not-allowed',
              opacity: canMap ? 1 : 0.6,
              bgcolor: canMap ? undefined : alpha(theme.palette.grey[100], 0.5),
              ...(hasStagingOutput && canMap ? {
                borderColor: theme.palette.success.main,
                boxShadow: `0 0 0 1px ${alpha(theme.palette.success.main, 0.3)}`,
              } : {}),
              '&:hover': canMap ? {
                borderColor: hasStagingOutput ? theme.palette.success.main : theme.palette.primary.main,
                boxShadow: theme.shadows[4],
              } : {},
            }}
          >
            <CardActionArea
              onClick={canMap ? onGenerateMapping : undefined}
              disabled={!canMap}
              sx={{ height: '100%', p: 2.5 }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <MappingIcon sx={{ fontSize: 20, color: canMap ? (hasStagingOutput ? 'success.main' : 'primary.main') : 'text.disabled' }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Generate Mapping
                  </Typography>
                </Box>
                {!canMap ? (
                  <Typography variant="body2" color="error.main" fontSize={12}>
                    Requires a target schema to be assigned.
                  </Typography>
                ) : hasStagingOutput ? (
                  <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                    Map staged output to your target schema. <strong style={{ color: theme.palette.success.main }}>Ready to go.</strong>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                    Map source data to your target schema using AI.
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BatchFlowCard;
